<script lang="ts" setup>
import { ref, computed, withDefaults } from 'vue'
import CurrencyInput from '@lahaus-roomie/roomie/src/components/RoomieCurrencyInput/index.vue'
import type { Props } from '@/components/SearchFilters/ModalContent/PriceRange/types'
import { DEFAULT_VALUES } from '@/components/SearchFilters/ModalContent/PriceRange/utils'
import MinMaxRange from '@/components/App/MinMaxRange/index.vue'
import { isUSCity } from '@/constants/us-cities'

const { t, locale } = useI18n()
const route = useRoute()

const emit = defineEmits(['change'])
const { $useLhId } = useNuxtApp()

const props = withDefaults(defineProps<Props>(), {
  minPriceLabel: '',
  maxPriceLabel: '',
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})

const touchedInputs = ref(false)
const minPrice = defineModel<number | null>('minPrice', { default: null })
const maxPrice = defineModel<number | null>('maxPrice', { default: null })
const isFocusingInputs = ref(false)
const sliderRangesBackup = ref<{ min: number | null, max: number | null }>({ min: 0, max: 0 })

// Check if this is a US city page to use USD values
const isUSCityPage = computed(() => {
  const cityParam = route.query.city as string | undefined
  return isUSCity(cityParam)
})

const effectiveLocale = computed((): 'co' | 'mx' | 'us' => {
  if (isUSCityPage.value) return 'us'
  return locale.value as 'co' | 'mx'
})

const defaultRangeValues = computed(() => DEFAULT_VALUES[effectiveLocale.value])

const priceLabels = computed((): { maxPriceLabel: string, minPriceLabel: string } => {
  return {
    maxPriceLabel: props.maxPriceLabel || t('maxLabel'),
    minPriceLabel: props.minPriceLabel || t('minLabel')
  }
})

const handleMinMaxChange = (evt: { minValue: number, maxValue: number }) => {
  minPrice.value = evt.minValue
  maxPrice.value = evt.maxValue
  if (touchedInputs.value) emit('change', { minPrice: minPrice.value, maxPrice: maxPrice.value })
  touchedInputs.value = false
}

const handleInputsFocus = () => {
  isFocusingInputs.value = true
  sliderRangesBackup.value.min = minPrice.value
  sliderRangesBackup.value.max = maxPrice.value
}

const handleInputsBlur = () => {
  isFocusingInputs.value = false
}

const onInput = () => {
  touchedInputs.value = true
}

const currencyInputLocale = computed(() => {
  if (isUSCityPage.value) return 'es-MX' // Use Spanish formatting for USD
  return ({ mx: 'es-MX', co: 'es-CO' }[locale.value as ('co' | 'mx')])
})

const currencyCode = computed(() => {
  if (isUSCityPage.value) return 'USD'
  return locale.value === 'mx' ? 'MXN' : 'COP'
})
</script>

<script lang="ts">
export default {
  name: 'PriceRange'
}
</script>

<template>
  <div class="pb-24">
    <div class="grid grid-cols-2 gap-12 mb-12">
      <CurrencyInput
        :id="`${id}-min-price-input`"
        v-model="minPrice"
        :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'escribir', C: 'precio minimo' })"
        :label="priceLabels.minPriceLabel"
        :locale="currencyInputLocale"
        :currency="isUSCityPage ? currencyCode : ''"
        autocomplete="off"
        @focus="handleInputsFocus"
        @blur="handleInputsBlur"
        @input="onInput" />

      <CurrencyInput
        :id="`${id}-max-price-input`"
        v-model="maxPrice"
        :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'escribir', C: 'precio maximo' })"
        :label="priceLabels.maxPriceLabel"
        autocomplete="off"
        :locale="currencyInputLocale"
        :currency="isUSCityPage ? currencyCode : ''"
        @focus="handleInputsFocus"
        @blur="handleInputsBlur"
        @input="onInput" />
    </div>

    <MinMaxRange
      :id="`${id}-range-slider`"
      :initial-min="isFocusingInputs ? sliderRangesBackup.min : minPrice"
      :initial-max="isFocusingInputs ? sliderRangesBackup.max : maxPrice"
      :min-range="defaultRangeValues.min"
      :max-range="defaultRangeValues.max"
      :interval="defaultRangeValues.interval"
      :gap="defaultRangeValues.interval"
      :data-attributes="{...dataAttributes, dataLh: { ...dataAttributes.dataLh, A: 'seleccionar rango de precios' }}"
      @update:model-value="handleMinMaxChange"
      @input="onInput" />
  </div>
</template>

<i18n lang="json" src="./i18n.json"></i18n>
